// 棚卸しの実数 CSV 取り込みの仕様。UI は移行画面と同じ CsvImportPanel を使う
// （①ファイル選択 → ②列の対応付け → ③dry-run で件数確認 → ④取り込む）。
//
// 仕様を関数で作るのは、エンドポイントが棚卸しID配下だから（移行系の3つは
// 固定 URL なので定数で持てる）。
//
// 🔴 固定文字列は【日本語のまま＝EN 辞書のキー】。描画時に CsvImportPanel が
// t() を通す（宣言時に t() を呼ぶと locale 確定より先に評価されて焼き付く）。
import {
  applyStocktakeCountCsv,
  previewStocktakeCountCsv,
  type StocktakeCountCsvResult,
} from "../api/stocktake_count_import";
import type { CsvImportSpec } from "../components/csvImportSpec";
import type { StocktakeItem } from "../api/stocktakes";
import type { StockyPublicBigIntId } from "../api/stocky_public_ids";
import { t } from "../lib/i18n";

const UTF8_BOM = "\uFEFF";
const FORMULA_PREFIX = /^[\x00-\x20]*[=+\-@]/;

/**
 * 入力用CSVは Shopify ID を正本キーにする。SKU は倉庫で行を見分けるための
 * 補助列で、危険な先頭文字は表計算ソフトの数式として実行されないようにする。
 */
function csvCell(value: string | number): string {
  let text = String(value);
  if (FORMULA_PREFIX.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** 現在の棚卸し範囲だけを、同じ画面へ再投入できる入力用CSVにする。 */
export function buildStocktakeCountInputCsv(items: StocktakeItem[]): string {
  const rows = ["Shopify ID,SKU,Counted"];
  for (const item of items) {
    rows.push(
      [item.shopify_variant_id, item.sku ?? "", item.counted_qty ?? ""]
        .map(csvCell)
        .join(","),
    );
  }
  return `${UTF8_BOM}${rows.join("\r\n")}\r\n`;
}

export function stocktakeCountCsvSpec(
  stocktakeId: StockyPublicBigIntId,
): CsvImportSpec<StocktakeCountCsvResult> {
  return {
    id: `stocktake-counts-${stocktakeId}`,
    title: "実数をCSVでまとめて入力",
    description:
      "数えた実数の CSV を取り込みます。ハンディ端末の書き出しや Excel の集計表をそのまま使えます。CSV に無い商品は「未カウント」のまま残るので、何回かに分けて取り込めます。1,000商品を超える場合、CSV が突合するのは現在の棚卸し範囲だけです。この範囲を反映後、次の範囲の棚卸しを開始してください。",
    // 🔴 在庫が動かないことは必ず先に言う。「取り込む」を押した時点で在庫が
    //    変わると思わせると、数え途中で押せなくなる（＝機能が使われない）。
    exportPath:
      "この取り込みでは在庫は変わりません。実数が入るだけで、Shopify への反映は「在庫へ反映する」を押したときだけです。",
    fields: [
      { key: "counted_qty", label: "実数の列（必須）", isRequired: true },
      { key: "sku", label: "SKU の列", isRequired: false },
      {
        key: "shopify_variant_id",
        label: "Shopify のバリアントIDの列",
        isRequired: false,
      },
    ],
    // 商品を特定できない CSV は取り込めない＝どちらかのキー列を必ず選ばせる。
    atLeastOneOf: ["sku", "shopify_variant_id"],
    readyHint:
      "実数の列と、商品を特定する列（SKU または Shopify のバリアントID）を選ぶと取り込めます。",
    preview: (fileBase64) => previewStocktakeCountCsv(stocktakeId, fileBase64),
    apply: (fileBase64, mapping, options) =>
      applyStocktakeCountCsv(stocktakeId, fileBase64, mapping, options),
    overwriteNoun: "実数",
    // 入力済みの実数が上書きされ得るので、件数は確認ダイアログに出す。
    overwriteCount: (result) => result.matched_items,
    writtenCount: (result) => result.matched_items,
    summarize: (result) =>
      t("実数を入れる商品 {count} 件", { count: result.matched_items }),
    resultTitle: (result) =>
      t("{count} 件の実数を取り込みました（在庫はまだ変わっていません）", {
        count: result.matched_items,
      }),
    unmatchedSkus: (result) => result.unmatched_keys,
  };
}
